import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ListTodo, Link, Users, BarChart3, Upload, Shield, Plus, Trash2 } from 'lucide-react';
import type { CalendarSettings, AvailabilityWindowSetting } from '../types';
import type {
  FocusTimeSettingsDetail,
  FocusTimeSettingsInput,
  CalendarCollectionDetail,
} from '@/lib/api';
import { Section, ToggleRow, Field, formatHour, HOURS, DAY_LABELS } from './settings-helpers';
import { SettingsGeneralSection } from './SettingsGeneralSection';
import { SettingsEventDefaultsSection } from './SettingsEventDefaultsSection';
import { SettingsDisplaySection } from './SettingsDisplaySection';
import { SettingsDailyPlanningSection } from './SettingsDailyPlanningSection';
import { SettingsFocusTimeSection } from './SettingsFocusTimeSection';

/* ---------- component ---------- */

interface SettingsSheetProps {
  open: boolean;
  onClose: () => void;
  settings: CalendarSettings;
  onUpdateSettings: (update: Partial<CalendarSettings>) => void;
  focusTimeSettings?: FocusTimeSettingsDetail | null;
  onUpdateFocusTime?: (input: FocusTimeSettingsInput) => Promise<unknown>;
  onGenerateFocusBlocks?: () => Promise<number>;
  onRegenerateFocusBlocks?: () => Promise<number>;
  onClearFocusBlocks?: () => Promise<number>;
  calendars?: CalendarCollectionDetail[];
}

export function SettingsSheet({
  open,
  onClose,
  settings,
  onUpdateSettings,
  focusTimeSettings,
  onUpdateFocusTime,
  onGenerateFocusBlocks,
  onRegenerateFocusBlocks,
  onClearFocusBlocks,
  calendars,
}: SettingsSheetProps) {
  const tf = settings.timeFormat ?? '12h';

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Calendar Settings</SheetTitle>
          <SheetDescription>Customize your calendar preferences.</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-8 pb-6">
          {/* ==== GENERAL ==== */}
          <SettingsGeneralSection settings={settings} onUpdateSettings={onUpdateSettings} />

          {/* ==== EVENT DEFAULTS ==== */}
          <SettingsEventDefaultsSection settings={settings} onUpdateSettings={onUpdateSettings} />

          {/* ==== DISPLAY ==== */}
          <SettingsDisplaySection settings={settings} onUpdateSettings={onUpdateSettings} />

          {/* ==== PLAN MY DAY & EVENING REVIEW ==== */}
          <SettingsDailyPlanningSection settings={settings} onUpdateSettings={onUpdateSettings} />

          {/* ==== FOCUS TIME ==== */}
          <SettingsFocusTimeSection
            settings={settings}
            focusTimeSettings={focusTimeSettings}
            onUpdateFocusTime={onUpdateFocusTime}
            onGenerateFocusBlocks={onGenerateFocusBlocks}
            onRegenerateFocusBlocks={onRegenerateFocusBlocks}
            onClearFocusBlocks={onClearFocusBlocks}
            calendars={calendars}
          />

          {/* ==== TASKS ==== */}
          <Section icon={ListTodo} title="Tasks">
            <ToggleRow
              label="Show task panel by default"
              checked={settings.showTaskPanel}
              onCheckedChange={(checked) => onUpdateSettings({ showTaskPanel: checked })}
            />

            <Field label="Default priority">
              <Select
                value={settings.defaultTaskPriority ?? 'medium'}
                onValueChange={(v) =>
                  onUpdateSettings({
                    defaultTaskPriority: v as 'high' | 'medium' | 'low',
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field label="Default estimated duration">
              <Select
                value={String(settings.defaultTaskDurationMinutes ?? 60)}
                onValueChange={(v) =>
                  onUpdateSettings({ defaultTaskDurationMinutes: parseInt(v, 10) })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="90">1.5 hours</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <ToggleRow
              label="Auto-schedule new tasks"
              description="Automatically find a time slot when creating a task."
              checked={settings.autoScheduleNewTasks ?? false}
              onCheckedChange={(checked) => onUpdateSettings({ autoScheduleNewTasks: checked })}
            />

            <Field label="Sort order">
              <Select
                value={settings.taskSortOrder ?? 'priority'}
                onValueChange={(v) =>
                  onUpdateSettings({ taskSortOrder: v as 'priority' | 'deadline' | 'created' })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="priority">Priority</SelectItem>
                  <SelectItem value="deadline">Deadline</SelectItem>
                  <SelectItem value="created">Created date</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field label="Show completed tasks">
              <Select
                value={settings.showCompletedTasks ?? 'never'}
                onValueChange={(v) =>
                  onUpdateSettings({ showCompletedTasks: v as 'never' | '24h' | '7d' })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="never">Never</SelectItem>
                  <SelectItem value="24h">Last 24 hours</SelectItem>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field label="Default deadline offset">
              <Select
                value={
                  settings.defaultTaskDeadlineOffset == null
                    ? '__none__'
                    : String(settings.defaultTaskDeadlineOffset)
                }
                onValueChange={(v) =>
                  onUpdateSettings({
                    defaultTaskDeadlineOffset: v === '__none__' ? null : parseInt(v, 10),
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No default</SelectItem>
                  <SelectItem value="1">1 day</SelectItem>
                  <SelectItem value="3">3 days</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </Section>

          {/* ==== SCHEDULING LINKS ==== */}
          <Section
            icon={Link}
            title="Scheduling Links"
            description="Defaults applied when creating a new scheduling link."
          >
            <Field label="Default meeting duration">
              <Select
                value={String(settings.defaultSchedulingDurationMinutes ?? 30)}
                onValueChange={(v) =>
                  onUpdateSettings({ defaultSchedulingDurationMinutes: parseInt(v, 10) })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="90">1.5 hours</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field label="Default buffer between meetings">
              <Select
                value={String(settings.defaultSchedulingBufferMinutes ?? 0)}
                onValueChange={(v) =>
                  onUpdateSettings({ defaultSchedulingBufferMinutes: parseInt(v, 10) })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">No buffer</SelectItem>
                  <SelectItem value="5">5 minutes</SelectItem>
                  <SelectItem value="10">10 minutes</SelectItem>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            {calendars && calendars.length > 0 && (
              <Field label="Default calendar for bookings">
                <Select
                  value={settings.defaultSchedulingCalendarId ?? '__default__'}
                  onValueChange={(v) =>
                    onUpdateSettings({
                      defaultSchedulingCalendarId: v === '__default__' ? null : v,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Default calendar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">Default calendar</SelectItem>
                    {calendars.map((cal) => (
                      <SelectItem key={cal.id} value={cal.id}>
                        {cal.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}

            <div>
              <Label className="text-[13px] mb-1.5 block">Default availability windows</Label>
              <div className="space-y-1.5">
                {(settings.defaultAvailabilityWindows ?? []).map((win, idx) => (
                  <div key={idx} className="flex items-center gap-1.5">
                    <Select
                      value={String(win.day)}
                      onValueChange={(v) => {
                        const updated = [...(settings.defaultAvailabilityWindows ?? [])];
                        updated[idx] = { ...updated[idx], day: parseInt(v, 10) };
                        onUpdateSettings({ defaultAvailabilityWindows: updated });
                      }}
                    >
                      <SelectTrigger className="w-[5.5rem] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DAY_LABELS.map((d, i) => (
                          <SelectItem key={i} value={String(i)}>
                            {d}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={String(win.startHour)}
                      onValueChange={(v) => {
                        const updated = [...(settings.defaultAvailabilityWindows ?? [])];
                        updated[idx] = { ...updated[idx], startHour: parseInt(v, 10) };
                        onUpdateSettings({ defaultAvailabilityWindows: updated });
                      }}
                    >
                      <SelectTrigger className="w-[5.5rem] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {HOURS.map((h) => (
                          <SelectItem key={h} value={String(h)}>
                            {formatHour(h, tf)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-muted-foreground">&ndash;</span>
                    <Select
                      value={String(win.endHour)}
                      onValueChange={(v) => {
                        const updated = [...(settings.defaultAvailabilityWindows ?? [])];
                        updated[idx] = { ...updated[idx], endHour: parseInt(v, 10) };
                        onUpdateSettings({ defaultAvailabilityWindows: updated });
                      }}
                    >
                      <SelectTrigger className="w-[5.5rem] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {HOURS.map((h) => (
                          <SelectItem key={h} value={String(h)}>
                            {formatHour(h, tf)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      onClick={() => {
                        const updated = (settings.defaultAvailabilityWindows ?? []).filter(
                          (_, i) => i !== idx,
                        );
                        onUpdateSettings({ defaultAvailabilityWindows: updated });
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => {
                    const updated: AvailabilityWindowSetting[] = [
                      ...(settings.defaultAvailabilityWindows ?? []),
                      { day: 1, startHour: 9, endHour: 17 },
                    ];
                    onUpdateSettings({ defaultAvailabilityWindows: updated });
                  }}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add window
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-[13px] mb-1 block">Default booking confirmation message</Label>
              <textarea
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-[13px] ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                rows={3}
                maxLength={500}
                placeholder="Optional message sent to attendees after booking..."
                value={settings.defaultBookingConfirmationMessage ?? ''}
                onChange={(e) =>
                  onUpdateSettings({ defaultBookingConfirmationMessage: e.target.value })
                }
              />
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {(settings.defaultBookingConfirmationMessage ?? '').length}/500
              </p>
            </div>
          </Section>

          {/* ==== PRODUCTIVITY ==== */}
          <Section
            icon={Shield}
            title="Productivity"
            description="Guardrails to protect your time and reduce meeting overload."
          >
            <Field label="Max meetings per day">
              <Select
                value={
                  settings.maxMeetingsPerDay == null
                    ? '__off__'
                    : String(settings.maxMeetingsPerDay)
                }
                onValueChange={(v) =>
                  onUpdateSettings({ maxMeetingsPerDay: v === '__off__' ? null : parseInt(v, 10) })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__off__">No limit</SelectItem>
                  {[3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Min break between events">
              <Select
                value={String(settings.minBreakBetweenEvents ?? 0)}
                onValueChange={(v) => onUpdateSettings({ minBreakBetweenEvents: parseInt(v, 10) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">None</SelectItem>
                  <SelectItem value="5">5 minutes</SelectItem>
                  <SelectItem value="10">10 minutes</SelectItem>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <ToggleRow
              label="Show conflict warnings"
              description="Warn when creating overlapping events."
              checked={settings.showConflictWarnings ?? true}
              onCheckedChange={(checked) => onUpdateSettings({ showConflictWarnings: checked })}
            />

            <ToggleRow
              label="Allow double-booking"
              checked={settings.allowDoubleBooking ?? true}
              onCheckedChange={(checked) => onUpdateSettings({ allowDoubleBooking: checked })}
            />
          </Section>

          {/* ==== FIND A TIME ==== */}
          <Section
            icon={Users}
            title="Find a Time"
            description="Defaults when searching for a time that works for everyone."
          >
            <Field label="Default search range">
              <Select
                value={String(settings.findTimeDefaultRangeDays ?? 7)}
                onValueChange={(v) =>
                  onUpdateSettings({ findTimeDefaultRangeDays: parseInt(v, 10) })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Next 7 days</SelectItem>
                  <SelectItem value="14">Next 14 days</SelectItem>
                  <SelectItem value="30">Next 30 days</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field label="Default meeting duration">
              <Select
                value={String(settings.findTimeDefaultDurationMinutes ?? 60)}
                onValueChange={(v) =>
                  onUpdateSettings({ findTimeDefaultDurationMinutes: parseInt(v, 10) })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="90">1.5 hours</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </Section>

          {/* ==== ANALYTICS ==== */}
          <Section icon={BarChart3} title="Analytics">
            <Field label="Default time range">
              <Select
                value={settings.analyticsDefaultPreset ?? '30d'}
                onValueChange={(v) =>
                  onUpdateSettings({
                    analyticsDefaultPreset: v as '7d' | '30d' | '3mo',
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="3mo">Last 3 months</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </Section>

          {/* ==== IMPORT ==== */}
          <Section icon={Upload} title="Import">
            <Field label="Default duplicate handling">
              <Select
                value={settings.importDefaultDuplicateStrategy ?? 'skip'}
                onValueChange={(v) =>
                  onUpdateSettings({
                    importDefaultDuplicateStrategy: v as 'skip' | 'overwrite' | 'create_new',
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">Skip duplicates</SelectItem>
                  <SelectItem value="overwrite">Overwrite existing</SelectItem>
                  <SelectItem value="create_new">Create new copies</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </Section>
        </div>
      </SheetContent>
    </Sheet>
  );
}
